import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi, setAuthToken } from "@/lib/api";

const TOKEN_KEY = "elevatebuild_trainee_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(Boolean(token));

    const persistToken = useCallback((nextToken) => {
        if (nextToken) {
            localStorage.setItem(TOKEN_KEY, nextToken);
        } else {
            localStorage.removeItem(TOKEN_KEY);
        }
        setAuthToken(nextToken);
        setTokenState(nextToken);
    }, []);

    // On mount: sync token to axios and fetch /me
    useEffect(() => {
        setAuthToken(token);
        if (!token) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        authApi
            .me()
            .then((u) => {
                if (!cancelled) setUser(u);
            })
            .catch(() => {
                if (!cancelled) {
                    persistToken(null);
                    setUser(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(
        async ({ email, password }) => {
            const data = await authApi.login({ email, password });
            persistToken(data.access_token);
            setUser(data.user);
            return data.user;
        },
        [persistToken],
    );

    const signup = useCallback(
        async (payload) => {
            const data = await authApi.signup(payload);
            persistToken(data.access_token);
            setUser(data.user);
            return data.user;
        },
        [persistToken],
    );

    const logout = useCallback(() => {
        persistToken(null);
        setUser(null);
    }, [persistToken]);

    const value = { user, token, loading, login, signup, logout, isAuthenticated: !!user };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" · ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
